<template>
  <div>
    <v-card>
      <v-card-title>
        Manage Users
      </v-card-title>

      <v-card-text>
        <v-row>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="filters.firstName"
              label="First Name"
              single-line
              hide-details
              dense
              outlined
              clearable
              @input="filterUsers"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="filters.lastName"
              label="Last Name"
              single-line
              hide-details
              dense
              outlined
              clearable
              @input="filterUsers"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="filters.email"
              label="Email"
              single-line
              hide-details
              dense
              outlined
              clearable
              @input="filterUsers"
            />
          </v-col>
          <v-col cols="12" md="2">
            <v-select
              v-model="filters.status"
              :items="statusOptions"
              label="Status"
              hide-details
              dense
              outlined
              clearable
              @change="filterUsers"
            />
          </v-col>
          <v-col cols="12" md="1">
            <v-btn color="primary" block @click="loadUsers">
              <v-icon>{{ icons.mdiRefresh }}</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>

      <v-data-table
        :headers="headers"
        :items="filteredUsers"
        :loading="isLoading"
        :items-per-page="15"
        class="elevation-1"
      >
        <template #item.name="{ item }">
          {{ item.firstName }} {{ item.lastName }}
        </template>

        <template #item.type="{ item }">
          <v-chip
            small
            :color="getUserTypeColor(item.type)"
            dark
          >
            {{ getUserTypeLabel(item.type) }}
          </v-chip>
        </template>

        <template #item.status="{ item }">
          <v-chip
            v-if="item.status"
            small
            :color="getStatusColor(item.status)"
            dark
          >
            {{ item.status }}
          </v-chip>
          <span v-else class="text--disabled">—</span>
        </template>

        <template #item.contentPacksCount="{ item }">
          <v-btn
            small
            color="primary"
            outlined
            class="content-pack-count-btn"
            @click="showContentPacksDialog(item)"
          >
            {{ item.contentPacksCount || 0 }}
          </v-btn>
        </template>

        <template #item.actions="{ item }">
          <div class="d-flex justify-end gap-2">
            <v-btn
              v-if="showPromoteToCoachButton(item)"
              small
              color="primary"
              @click="promoteToCoach(item)"
            >
              Promote to Coach
            </v-btn>
            <v-btn
              v-if="showApproveButton(item)"
              small
              color="success"
              @click="approveCoach(item)"
            >
              Approve
            </v-btn>

            <v-btn
              v-if="showSwitchToPlayerButton(item)"
              small
              color="primary"
              @click="switchToPlayer(item)"
            >
              Switch to Player
            </v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="isContentPacksDialogVisible" max-width="720">
      <v-card>
        <v-card-title>
          User Content Packs
        </v-card-title>
        <v-card-text>
          <div class="text-body-2 mb-4">
            {{ selectedUserLabel }}
          </div>

          <v-list v-if="selectedUserContentPacks.length" dense class="py-0">
            <v-list-item class="content-pack-header px-0">
              <v-list-item-content>
                <v-list-item-title class="text-caption font-weight-bold">
                  Content Pack
                </v-list-item-title>
              </v-list-item-content>
              <div class="content-pack-purchased text-caption font-weight-bold">
                Purchased
              </div>
              <v-list-item-action />
            </v-list-item>
            <v-list-item v-for="contentPack in selectedUserContentPacks" :key="contentPack.id">
              <v-list-item-content>
                <v-list-item-title>{{ contentPack.name }}</v-list-item-title>
              </v-list-item-content>
              <div class="content-pack-purchased text-body-2">
                {{ formatPurchased(contentPack.purchased) }}
              </div>
              <v-list-item-action>
                <v-btn icon small @click="confirmRemoveContentPack(contentPack)">
                  <v-icon small>
                    {{ icons.mdiDeleteOutline }}
                  </v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
          <div v-else class="text-body-2 grey--text">
            No content packs assigned.
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeContentPacksDialog">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="isDeleteContentPackDialogVisible" max-width="560">
      <v-card>
        <v-card-title>Remove Content Pack</v-card-title>
        <v-card-text>
          <p>{{ deleteContentPackMessage }}</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="isDeleteContentPackDialogVisible = false">
            Cancel
          </v-btn>
          <v-btn color="primary" :loading="isDeletingContentPack" @click="removeContentPack">
            Continue
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="isCoachApprovalDialogVisible" max-width="480">
      <v-card>
        <v-card-title>Approve Coach</v-card-title>
        <v-card-text>
          Would you like to send an email for this approval?
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeCoachApprovalDialog">
            Cancel
          </v-btn>
          <v-btn color="primary" :loading="isApprovingCoach" @click="confirmCoachApproval">
            Accept
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mdiDeleteOutline, mdiRefresh } from '@mdi/js'
import {
  computed, ref, onMounted,
} from 'vue'
import useUsers from '@/composables/api/users'
import useContentPackUsers from '@/composables/api/contentPackUsers'
import useContentPackAcademies from '@/composables/api/contentPackAcademies'
import { USER_STATUS, USER_TYPE } from '@/enums/enums'

export default {
  name: 'ManageCoaches',

  setup() {
    const {
      users, isLoading, fetchUsers, updateUser,
    } = useUsers()
    const { fetchContentPacksByUserId, deleteContentPackUser } = useContentPackUsers()
    const { formatPurchased } = useContentPackAcademies()

    const filters = ref({
      firstName: '',
      lastName: '',
      email: '',
      status: null,
    })

    const statusOptions = [
      { text: 'All', value: null },
      { text: 'Approved', value: 'APPROVED' },
      { text: 'Pending', value: 'PENDING' },
    ]

    const filteredUsers = ref([])
    const isContentPacksDialogVisible = ref(false)
    const isDeleteContentPackDialogVisible = ref(false)
    const isCoachApprovalDialogVisible = ref(false)
    const selectedUser = ref(null)
    const selectedUserContentPacks = ref([])
    const contentPackToDelete = ref(null)
    const isDeletingContentPack = ref(false)
    const coachToApprove = ref(null)
    const isApprovingCoach = ref(false)

    const headers = [
      { text: 'Name', value: 'name', sortable: true },
      { text: 'Email', value: 'email', sortable: true },
      { text: 'User Type', value: 'type', sortable: true },
      { text: 'Status', value: 'status', sortable: true },
      {
        text: 'Content Packs #', value: 'contentPacksCount', sortable: true, align: 'center',
      },
      {
        text: 'Actions', value: 'actions', sortable: false, align: 'end', width: '250px',
      },
    ]

    const selectedUserLabel = computed(() => {
      if (!selectedUser.value) return ''

      return `${[selectedUser.value.firstName, selectedUser.value.lastName].filter(Boolean).join(' ')}${selectedUser.value.email ? ` (${selectedUser.value.email})` : ''}`
    })

    const deleteContentPackMessage = computed(() => {
      if (!selectedUser.value || !contentPackToDelete.value) return ''

      return `Are you sure you want to remove content pack "${contentPackToDelete.value.name}" from user "${selectedUserLabel.value}"?`
    })

    const filterUsers = () => {
      let result = users.value

      if (filters.value.firstName) {
        const search = filters.value.firstName.toLowerCase()
        result = result.filter(user => user.firstName?.toLowerCase().includes(search))
      }

      if (filters.value.lastName) {
        const search = filters.value.lastName.toLowerCase()
        result = result.filter(user => user.lastName?.toLowerCase().includes(search))
      }

      if (filters.value.email) {
        const search = filters.value.email.toLowerCase()
        result = result.filter(user => user.email?.toLowerCase().includes(search))
      }

      if (filters.value.status) {
        result = result.filter(user => user.status?.toUpperCase() === filters.value.status)
      }

      filteredUsers.value = result
    }

    const loadUsers = async () => {
      try {
        await fetchUsers()
        filterUsers()
      } catch (error) {
        console.error('Failed to load users:', error)
      }
    }

    const getUserTypeLabel = type => {
      if (!type) return 'Unknown'

      switch (type.toLowerCase()) {
        case USER_TYPE.ATHLETE:
          return 'Player'
        case USER_TYPE.COACH:
          return 'Coach'
        case USER_TYPE.INACTIVE:
          return 'Inactive'
        default:
          return type
      }
    }

    const getUserTypeColor = type => {
      if (!type) return 'grey'

      switch (type.toLowerCase()) {
        case USER_TYPE.ATHLETE:
          return 'blue'
        case USER_TYPE.COACH:
          return 'green'
        case USER_TYPE.INACTIVE:
          return 'grey'
        default:
          return 'grey'
      }
    }

    const getStatusColor = status => {
      if (!status) return 'grey'

      switch (status.toUpperCase()) {
        case USER_STATUS.APPROVED:
          return 'success'
        case USER_STATUS.PENDING:
          return 'warning'
        default:
          return 'grey'
      }
    }

    // Button visibility logic:
    // 1) If user is of type athlete (player) and no pending status - Only "Promote to coach" button is visible
    const showPromoteToCoachButton = user => user.type?.toLowerCase() === USER_TYPE.ATHLETE
             && user.status?.toUpperCase() !== USER_STATUS.PENDING

    // 2) If user has status Pending - only "Approve" button is visible (regardless of type)
    const showApproveButton = user => user.status?.toUpperCase() === USER_STATUS.PENDING

    // 3) If user is of type coach and is approved - Switch to Player buttom should be visible
    const showSwitchToPlayerButton = user => user.type?.toLowerCase() === USER_TYPE.COACH
        && user.status?.toUpperCase() === USER_STATUS.APPROVED

    // 4) No buttons visible for Inactive users or Coaches with status Approved or when status field is empty
    // This is handled by the two functions above returning false

    const promoteToCoach = async user => {
      try {
        const updatedUser = {
          ...user,
          type: 'coach',
        }
        await updateUser(updatedUser)
        await loadUsers()
      } catch (error) {
        console.error('Failed to promote user to coach:', error)
      }
    }

    const switchToPlayer = async user => {
      try {
        const updatedUser = {
          ...user,
          type: 'athlete',
        }
        await updateUser(updatedUser)
        await loadUsers()
      } catch (error) {
        console.error('Failed to switch a coach to a pleyer:', error)
      }
    }

    const approveCoach = user => {
      coachToApprove.value = user
      isCoachApprovalDialogVisible.value = true
    }

    const closeCoachApprovalDialog = () => {
      isCoachApprovalDialogVisible.value = false
      coachToApprove.value = null
    }

    const confirmCoachApproval = async () => {
      if (!coachToApprove.value) return

      isApprovingCoach.value = true
      try {
        const updatedUser = {
          ...coachToApprove.value,
          status: USER_STATUS.APPROVED,
          sendCoachApprovalEmail: true,
        }
        await updateUser(updatedUser)
        closeCoachApprovalDialog()
        await loadUsers()
      } catch (error) {
        console.error('Failed to approve coach:', error)
      } finally {
        isApprovingCoach.value = false
      }
    }

    const showContentPacksDialog = async user => {
      selectedUser.value = user
      selectedUserContentPacks.value = await fetchContentPacksByUserId(user.id)
      isContentPacksDialogVisible.value = true
    }

    const closeContentPacksDialog = () => {
      isContentPacksDialogVisible.value = false
      selectedUser.value = null
      selectedUserContentPacks.value = []
    }

    const confirmRemoveContentPack = contentPack => {
      contentPackToDelete.value = contentPack
      isDeleteContentPackDialogVisible.value = true
    }

    const removeContentPack = async () => {
      if (!selectedUser.value || !contentPackToDelete.value) return

      isDeletingContentPack.value = true
      try {
        await deleteContentPackUser(selectedUser.value.id, contentPackToDelete.value.id, { silent: true })
        selectedUserContentPacks.value = selectedUserContentPacks.value.filter(item => item.id !== contentPackToDelete.value.id)
        const updatedUsers = users.value.map(user => (
          user.id === selectedUser.value.id
            ? { ...user, contentPacksCount: Math.max((user.contentPacksCount || 0) - 1, 0) }
            : user
        ))
        filteredUsers.value = updatedUsers
        users.value = updatedUsers
        isDeleteContentPackDialogVisible.value = false
        contentPackToDelete.value = null
      } finally {
        isDeletingContentPack.value = false
      }
    }

    onMounted(async () => {
      await loadUsers()
    })

    return {
      filters,
      statusOptions,
      filteredUsers,
      headers,
      isLoading,
      isContentPacksDialogVisible,
      isDeleteContentPackDialogVisible,
      isCoachApprovalDialogVisible,
      selectedUserContentPacks,
      selectedUserLabel,
      deleteContentPackMessage,
      isDeletingContentPack,
      isApprovingCoach,
      icons: {
        mdiDeleteOutline,
        mdiRefresh,
      },
      filterUsers,
      loadUsers,
      getUserTypeLabel,
      getUserTypeColor,
      getStatusColor,
      showPromoteToCoachButton,
      showSwitchToPlayerButton,
      showApproveButton,
      promoteToCoach,
      approveCoach,
      closeCoachApprovalDialog,
      confirmCoachApproval,
      switchToPlayer,
      showContentPacksDialog,
      closeContentPacksDialog,
      confirmRemoveContentPack,
      removeContentPack,
      formatPurchased,
    }
  },
}
</script>

<style scoped>
.gap-2 {
  gap: 8px;
}

.content-pack-count-btn {
  min-width: 44px;
  font-weight: 600;
}

.content-pack-header {
  min-height: 28px;
}

.content-pack-purchased {
  min-width: 72px;
  text-align: center;
}
</style>
